import { ApplicationController } from "../support/application-controller";
import { addEvent, removeEvent, replaceContentInNode, replaceNodeChildrenWithNode } from "../support/util";

export default class extends ApplicationController {
    static targets = [
        "modalHolder",
        "modalBackground",
        "modalDialog",
        "dialogTemplate"
    ];

    get accessibilityController() {
        return this.getControllerByIdentifier('accessibility')
    }

    initialize() {
        this.isOpen = false;
        this.previouslySelectedElement = undefined
    }

    connect() {
        window.hasModals = false;
        if (this._dialogHasContent()) {
            this._toggleVisibility(true);
        }
        addEvent(document, 'keyup', this.onEscapeCallback);
    }

    disconnect() {
        removeEvent(document, 'keyup', this.onEscapeCallback);
    }

    onEscapeCallback = event => {
        if (event.keyCode === 27 && this.isOpen) {
            this._toggleVisibility();
        }
    };

    showFromTemplate(e) {
        e.preventDefault();
        e.stopPropagation();
        const modalTemplateId = e.currentTarget.dataset.modalTemplateId;
        this.loadModalTemplateToTarget(modalTemplateId);
    }

    loadModalTemplateToTarget(modalTemplateId) {
        const templateTarget =
            this.dialogTemplateTargets
                .find(dialogTemplateTarget => dialogTemplateTarget.dataset.modalTemplateId === modalTemplateId);

        const templateClone = templateTarget.content.cloneNode(true);

        replaceNodeChildrenWithNode(this.modalDialogTarget, templateClone);
        if (this._dialogHasContent()) {
            this._toggleVisibility(true);
            this.accessibilityController.lockFocusInsideOverlay(this.modalHolderTarget);
        }
    }

    submit(e) {
        e.preventDefault();
        this.modalHolderTarget.classList.add('modal-holder--disabled');

        const url = e.target.action;
        const followUpModalId = e.target.dataset.onModalSubmittedNextModalTemplateId;
        const data = new FormData(e.target);

        this.railsPost({ url, data })
            .then(() => {
                this.modalHolderTarget.classList.remove('modal-holder--disabled');
                if (followUpModalId) {
                    this.loadModalTemplateToTarget(followUpModalId);
                }
            })
            .catch(response => {
                if ([400, 403].includes(response.status) && response.responseText) {
                    const body = JSON.parse(response.responseText);
                    const errorTarget = document.getElementById('modal-error');
                    if (errorTarget)
                        errorTarget.innerHTML = body.error;
                    this.modalHolderTarget.classList.remove("modal-holder--disabled");
                    const controlsTarget = document.getElementById('modal-controls');
                    if (controlsTarget) {
                        controlsTarget.style.display = 'none';
                    }
                } else {
                    throw new Error(response.responseText);
                }
            });
    }

    close(e) {
        e.preventDefault();
        this._hideModal();
        const redirectToUrl = e.target.dataset.redirectTo;
        if (redirectToUrl) {
            Turbolinks.visit(redirectToUrl);
        }
    }

    clickAwayClose(e) {
        if (e.target !== this.modalBackgroundTarget) return;
        this._hideModal();
    }

    _toggleVisibility(force = false) {
        this.modalHolderTarget.classList.toggle("modal-holder--visible", force);
        window.hasModals = !window.hasModals;
        this.isOpen = !this.isOpen;
    }

    _hideModal() {
        replaceContentInNode(this.modalDialogTarget, '');
        this._toggleVisibility();
        this.accessibilityController.unlockFocusFromOverlay();
        // https://socex-charts.atlassian.net/browse/SOCEX-1052
        // This timeout was intentionally added to prevent the bug described in this Jira card.
        // The problem is triggered in the moment of returning the focus from modal elements to its container (list element).
        // First part of the problem is the way how had we nested more options button, the dropdown triggered by that button
        // and the modal opened by dropdown items buttons. Second issue is the fact that focusing parent on modal close action
        // obviously is triggered before previous keyboard (enter key) event is propagated. Third, and maybe most important
        // aspect is the fact that originating event is synthetic and produced by stimulus-js and is always click (never key) event.
        // Solution we’ve found as a quick patch is to postpone returning focus. 200ms of timeout was empirically defined.
        // If you doubt this patch, first try just shortening timeout to 10 or 0ms and check for the bug.
        setTimeout(() => {
            if (this.previouslySelectedElement) {
                this._returnFocus(this.previouslySelectedElement.parentElement);
            }
        }, 200);
    }

    _dialogHasContent() {
        return this.modalDialogTarget.innerHTML && this.modalDialogTarget.innerHTML.trim().length > 0;
    }

    // Focus helper, to focus last element focused before opening modal
    // Recursively seek for parent element explicitly defined to be screen reader focusable (tabindex >= 0)
    _returnFocus(element) {
        if (!element) return;
        if (element.tabIndex > -1) { // -1 is default for DOM element. 0 is focusable
            element.focus();
        } else {
            this._returnFocus(element.parentElement);
        }
    }
}
